import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "next-i18next";
import {
  Form,
  Listbox,
  Button,
  Feedback,
  LoadingSpinner,
} from "@talladega/components";
import useCreateLeadMutation from "@modules/leads/hooks/useCreateLeadMutation";
import { ArrowRightIcon } from "@talladega/icons";
import {
  customerValidation,
  cloudflareImagesLoader,
  GetBackgroundStyle,
  IsNodeStructure,
} from "@talladega/helpers";
import { useWindowDimensions } from "@talladega/hooks";
import classNames from "classnames";
import TextLink from "@common/components/TextLink";
import {
  BaseVehicleDetail,
  VehicleDetail,
} from "@modules/vehicle/types/VehicleDetail";
import {
  CmsBackgroundType,
  CmsHeadingGroupType,
  CheckboxFieldLayout,
  ConsentFieldLayout,
  DropdownFieldLayout,
  EmailFieldLayout,
  GravityFormInput,
  GravityFormLayout,
  NameFieldLayout,
  PhoneFieldLayout,
  SectionBreakFieldLayout,
  SingleLineInputFieldLayout,
  TextAreaFieldLayout,
  BackgroundImageGroup,
} from "@talladega/types";
import {
  CmsHeading,
  FormContentHeader,
  FormSubmissionModal,
  FormSelectorBackgroundImage,
} from "@talladega/cms";
import useCreateGravityFormEntryMutation from "../hooks/useCreateGravityFormEntryMutation";
import { sizeClass } from "../types/SizeClass";
import { DEFAULT_FORM_SELECTOR_BACKGROUND_WIDTH } from "../constants/BackgroundImages";
import {
  getCheckboxNames,
  getDropdownName,
  getEmailFieldName,
  getNameFieldName,
  getPhoneFieldName,
  sanitizeFieldName,
} from "../helpers/gravityFormsFieldName";

type Props = {
  form: GravityFormLayout["form"];
  formId: number;
  heading: CmsHeadingGroupType | null;
  subheading: CmsHeadingGroupType | null;
  vinsolutionsId?: number;
  campaignTag: string;
  backgroundImageGroup?: BackgroundImageGroup;
  background?: CmsBackgroundType;
  confirmationText?: string;
  submitButtonText?: string;
  isPopup?: boolean;
  onSubmit?: (formInput: GravityFormInput) => void;
  onError?: () => void;
  vehicle?: VehicleDetail | BaseVehicleDetail;
};

const GravityForm = ({
  form,
  formId,
  heading,
  subheading,
  // vinsolutionsId,
  // campaignTag,
  backgroundImageGroup,
  background,
  confirmationText,
  submitButtonText,
  isPopup = false,
  onSubmit,
}: // onError,
// vehicle
Props) => {
  const createLeadMutation = useCreateLeadMutation();
  const createGravityFormEntryMutation = useCreateGravityFormEntryMutation();
  const { t } = useTranslation(["common"]);
  const [width, setWidth] = useState(0);
  const { width: w } = useWindowDimensions();
  useEffect(() => {
    setWidth(w);
  }, [w]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);
  const [selectedDropdownValues, setSelectedDropdownValues] = useState<
    { name: string; value: { name: string; id: string } }[]
  >([]);
  const [selectedCheckboxValues, setSelectedCheckboxValues] = useState<
    {
      [key: string]: boolean;
    }[]
  >([]);

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  const { sourceUrl } =
    (IsNodeStructure(backgroundImageGroup?.image)
      ? backgroundImageGroup?.image?.node
      : backgroundImageGroup?.image?.[0]) ?? {};

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitted },
    setValue,
  } = useForm<GravityFormInput>({
    mode: "onSubmit",
  });

  const handleDropdownChange = (
    name: string,
    value: { name: string; id: string }
  ) => {
    setSelectedDropdownValues((prevState) => {
      // Check if an item with the same name already exists in the array
      const index = prevState.findIndex((item) => item.name === name);
      if (index !== -1) {
        // Update the existing item
        return [
          ...prevState.slice(0, index),
          { name, value },
          ...prevState.slice(index + 1),
        ];
      }
      // Add a new item to the array
      return [...prevState, { name, value }];
    });
  };

  const handleCheckboxChange = (
    name: string,
    value: boolean,
    inputName: string
  ) => {
    setSelectedCheckboxValues((prevState) => {
      if (value) {
        // Remove any existing entry with the same input name
        const filteredState = prevState.filter(
          (checkboxValues) =>
            !Object.keys(checkboxValues).some((key) => {
              const keyParts = key.split("-");
              return keyParts[0] === inputName;
            })
        );

        // Add the new entry
        return [...filteredState, { [name]: true }];
      }
      // If the value is false, remove the entry
      return prevState.filter(
        (checkboxValues) =>
          !Object.prototype.hasOwnProperty.call(checkboxValues, name)
      );
    });
  };

  const validateCheckboxFields = (formInput: GravityFormInput) => {
    let checkedRequiredCheckboxes = 0;
    const requiredCheckboxes = form.formFields.nodes.filter((field) => {
      return (
        field.type === "CHECKBOX" && (field as CheckboxFieldLayout).isRequired
      );
    });
    requiredCheckboxes.forEach((field) => {
      const checkboxNames = getCheckboxNames(field as CheckboxFieldLayout);
      if (checkboxNames.some((input) => formInput[input] === true)) {
        checkedRequiredCheckboxes += 1;
      }
    });
    return checkedRequiredCheckboxes === requiredCheckboxes.length;
  };

  const handleFormSubmit = async (formInput: GravityFormInput) => {
    if (!validateCheckboxFields(formInput)) return;

    // TODO: Comment out lead submission for now. Will need to implement this later.
    // let leadMessage = "";
    // let userSelectedDealerId;

    // if (formInput?.message && formInput?.referredBy) {
    //   leadMessage = `Referred by: ${formInput.referredBy}\n`;
    // }
    // // Filter out all known fields and leave the rest as a message
    // Object.entries(formInput)
    //   .filter(
    //     ([key]) =>
    //       key !== "firstName" &&
    //       key !== "lastName" &&
    //       key !== "email" &&
    //       key !== "phoneNumber" &&
    //       key !== "purpose" &&
    //       key !== "chosenOffer" &&
    //       key !== "referredBy" &&
    //       key !== "friendsFirstName" &&
    //       key !== "friendsLastName" &&
    //       key !== "referralEmail" &&
    //       key !== "referralPhoneNumber" &&
    //       key !== "preferredLocation" &&
    //       key !== "consent"
    //   )
    //   .forEach(([key, value]) => {
    //     if (
    //       typeof value === "object" &&
    //       Object.prototype.hasOwnProperty.call(value, "name")
    //     ) {
    //       leadMessage += `${key}: ${(value as { name: string }).name}\n`;
    //       return;
    //     }
    //     if (key.includes("-")) {
    //       if (value) leadMessage += `${key} Selected\n`;
    //       return;
    //     }
    //     leadMessage += `${key}: ${value ?? "Not Selected"}\n`;
    //   });

    // createLeadMutation.mutate(
    //   {
    //     firstName: formInput.firstName,
    //     lastName: formInput.lastName,
    //     phoneNumber: formInput.phoneNumber || undefined,
    //     email: formInput.email,
    //     friendsFirstname: formInput.friendsFirstName,
    //     friendsLastname: formInput.friendsLastName,
    //     referralEmail: formInput.referralEmail,
    //     referralPhoneNumber: formInput.referralPhoneNumber,
    //     inquiry: formInput.purpose?.name,
    //     chosenOffer: formInput.chosenOffer?.name,
    //     originUrl: document.location.href,
    //     leadDescription: campaignTag,
    //     userSelectedDealerId,
    //     vinSolutionsDealerId: vinsolutionsId,
    //     message: leadMessage,
    //     vehicleId: vehicle?.id,
    //     userDataConsentGiven: true,
    //   },
    //   {
    //     onSuccess: () => {
    //       reset();
    //       setSelectedDropdownValues([]);
    //       setSelectedCheckboxValues([]);
    //       if (!isPopup) setIsSubmitModalOpen(true);
    //       if (onSubmit) onSubmit(formInput);
    //     },
    //     onError: () => {
    //       onError?.();
    //     },
    //   }
    // );

    createGravityFormEntryMutation.mutate({
      formId,
      formInput,
      formFields: form.formFields.nodes,
    });

    reset();
    setSelectedDropdownValues([]);
    setSelectedCheckboxValues([]);
    if (!isPopup) setIsSubmitModalOpen(true);
    if (onSubmit) onSubmit(formInput);
  };
  return (
    <div
      className="no-scrollbar relative overflow-visible bg-cover bg-center"
      style={background && GetBackgroundStyle(background, baseDomain)}
    >
      <LoadingSpinner isLoading={createLeadMutation.isPending} isOverlay />
      {isSubmitModalOpen && (
        <FormSubmissionModal
          isOpen={isSubmitModalOpen}
          setState={setIsSubmitModalOpen}
          modalDescription={confirmationText}
        />
      )}
      <div
        className={classNames({
          "bg-no-repeat": backgroundImageGroup?.image,
          "bg-[center_right_-16rem]":
            backgroundImageGroup?.imagePosition === "Right" &&
            width < sizeClass["2xl"] &&
            width >= sizeClass.xl,
          "bg-[center_right_-8rem]":
            backgroundImageGroup?.imagePosition === "Right" &&
            width < sizeClass["3xl"] &&
            width >= sizeClass["2xl"],
          "bg-[center_right_1rem]":
            backgroundImageGroup?.imagePosition === "Right" &&
            width < sizeClass["4xl"] &&
            width >= sizeClass["3xl"],
          "bg-[center_right_14rem]":
            backgroundImageGroup?.imagePosition === "Right" &&
            width >= sizeClass["4xl"],

          "bg-[center_left_-14rem]":
            backgroundImageGroup?.imagePosition === "Left" &&
            width < sizeClass["2xl"] &&
            width >= sizeClass.xl,
          "bg-[center_left_-6rem]":
            backgroundImageGroup?.imagePosition === "Left" &&
            width < sizeClass["3xl"] &&
            width >= sizeClass["2xl"],
          "bg-[center_left_2rem]":
            backgroundImageGroup?.imagePosition === "Left" &&
            width < sizeClass["4xl"] &&
            width >= sizeClass["3xl"],
          "bg-[center_left_16rem]":
            backgroundImageGroup?.imagePosition === "Left" &&
            width >= sizeClass["4xl"],
        })}
        style={
          width >= sizeClass.xl && sourceUrl
            ? {
                backgroundImage: `url(${cloudflareImagesLoader({
                  src: sourceUrl || "",
                  width: DEFAULT_FORM_SELECTOR_BACKGROUND_WIDTH,
                  baseDomain,
                })})`,
              }
            : {}
        }
      >
        <div
          className={classNames(
            "relative mx-auto flex max-w-page items-center lg:mx-0 xl:mx-auto",
            {
              "py-10": !isPopup,
              "flex-col": backgroundImageGroup,
              "items-center px-5 lg:items-stretch":
                !backgroundImageGroup ||
                !backgroundImageGroup.image ||
                !sourceUrl,
              "px-5 lg:flex-row lg:pl-5 lg:pr-0 xl:px-5":
                backgroundImageGroup?.imagePosition === "Right" &&
                backgroundImageGroup.image &&
                sourceUrl,
              "px-5 lg:flex-row-reverse lg:pl-0 lg:pr-5 xl:px-5":
                backgroundImageGroup?.imagePosition === "Left" &&
                backgroundImageGroup.image &&
                sourceUrl,
            }
          )}
        >
          <div
            className={classNames("", {
              "flex-1 lg:basis-[60%] xl:basis-[50%]":
                backgroundImageGroup?.image,
            })}
          >
            <div className="pb-6 text-center">
              {heading && (
                <CmsHeading
                  headingLevel={heading.headingGroup?.headingLevel}
                  textSize={heading.headingGroup?.textSize}
                  text={heading.headingGroup?.text}
                  htmlId={heading.headingGroup?.htmlId}
                />
              )}
              {subheading && (
                <div className="mt-4 lg:mt-8">
                  <CmsHeading
                    headingLevel={subheading.headingGroup?.headingLevel}
                    textSize={subheading.headingGroup?.textSize}
                    text={subheading.headingGroup?.text}
                    htmlId={subheading.headingGroup?.htmlId}
                  />
                </div>
              )}
            </div>
            {!isPopup && (
              <FormContentHeader
                title={form.title}
                description={form.description}
              />
            )}
            <form
              className="my-5 flex flex-col gap-5 md:grid md:grid-cols-2"
              onSubmit={handleSubmit(handleFormSubmit)}
            >
              {form.formFields.nodes.map((field) => {
                switch (field.type) {
                  case "NAME": {
                    const nameField = field as NameFieldLayout;
                    return nameField.inputs.map((input) => {
                      const inputLabel = getNameFieldName(
                        input.label,
                        input.customLabel
                      );
                      if (!input.isHidden) {
                        if (input.label === "First") {
                          return (
                            <div
                              key={`${nameField.databaseId}-${inputLabel}`}
                              className={nameField.cssClass ?? ""}
                            >
                              <Form.FirstName
                                id={inputLabel}
                                autoComplete={input.autocompleteAttribute}
                                label={input.customLabel || input.label}
                                placeholder={input.placeholder}
                                fieldError={errors[inputLabel]}
                                {...register(inputLabel, {
                                  ...customerValidation.firstName,
                                  required: {
                                    value: nameField.isRequired,
                                    message:
                                      nameField.errorMessage ||
                                      t("common:required"),
                                  },
                                  validate: {
                                    valid: (value) =>
                                      customerValidation.firstName.validate.valid(
                                        value?.toString()
                                      ),
                                  },
                                })}
                              />
                            </div>
                          );
                        }
                        if (input.label === "Last") {
                          return (
                            <div
                              key={`${nameField.databaseId}-${inputLabel}`}
                              className={nameField.cssClass ?? ""}
                            >
                              <Form.LastName
                                id={inputLabel}
                                autoComplete={input.autocompleteAttribute}
                                label={input.customLabel || input.label}
                                placeholder={input.placeholder}
                                fieldError={errors[inputLabel]}
                                {...register(inputLabel, {
                                  ...customerValidation.lastName,
                                  required: {
                                    value: nameField.isRequired,
                                    message:
                                      nameField.errorMessage ||
                                      t("common:required"),
                                  },
                                  validate: {
                                    valid: (value) =>
                                      customerValidation.lastName.validate.valid(
                                        value?.toString()
                                      ),
                                  },
                                })}
                              />
                            </div>
                          );
                        }
                      }
                      return null;
                    });
                  }
                  case "EMAIL": {
                    const emailField = field as EmailFieldLayout;
                    const emailInputLabel = getEmailFieldName(
                      emailField.inputs[0].label,
                      emailField.inputs[0].customLabel
                    );
                    return (
                      <div
                        key={emailField.databaseId}
                        className={emailField.cssClass ?? ""}
                      >
                        <Form.Email
                          id={emailInputLabel}
                          autoComplete={
                            emailField.inputs[0].autocompleteAttribute
                          }
                          label={
                            emailField.inputs[0].customLabel ||
                            emailField.inputs[0].label
                          }
                          placeholder={emailField.inputs[0].placeholder}
                          fieldError={errors[emailInputLabel]}
                          {...register(emailInputLabel, {
                            ...customerValidation.email,
                            required: {
                              value: emailField.isRequired,
                              message:
                                emailField.errorMessage || t("common:required"),
                            },
                            validate: {
                              valid: (value) =>
                                customerValidation.email.validate.valid(
                                  value?.toString()
                                ),
                            },
                          })}
                        />
                      </div>
                    );
                  }
                  case "PHONE": {
                    const phoneField = field as PhoneFieldLayout;
                    const phoneInputLabel = getPhoneFieldName(phoneField.label);
                    return (
                      <div
                        key={phoneField.databaseId}
                        className={phoneField.cssClass ?? ""}
                      >
                        <Form.Phone
                          id={phoneInputLabel}
                          autoComplete={phoneField.autocompleteAttribute}
                          label={phoneField.label}
                          placeholder={phoneField.placeholder}
                          fieldError={errors[phoneInputLabel]}
                          {...register(phoneInputLabel, {
                            ...customerValidation.phoneNumber,
                            required: {
                              value: phoneField.isRequired,
                              message:
                                phoneField.errorMessage || t("common:required"),
                            },
                          })}
                        />
                      </div>
                    );
                  }
                  case "SELECT": {
                    const dropdownField = field as DropdownFieldLayout;
                    const dropdownName = getDropdownName(dropdownField.label);
                    const selectedItem = selectedDropdownValues.find(
                      (item) => item.name === dropdownName
                    );
                    return (
                      <div
                        key={dropdownField.databaseId}
                        className={dropdownField.cssClass ?? ""}
                      >
                        <Controller
                          control={control}
                          rules={{
                            onChange: (e) =>
                              handleDropdownChange(
                                dropdownName,
                                e.target.value
                              ),
                            required: {
                              value: dropdownField.isRequired,
                              message:
                                dropdownField.errorMessage ||
                                t("common:required"),
                            },
                          }}
                          name={dropdownName}
                          render={({ field: { onChange, ref } }) => (
                            <Listbox
                              keyOption={(i) => i.id}
                              valueOption={(i) => i}
                              label={dropdownField.label}
                              displayOption={(i) => i?.name || ""}
                              onChange={onChange}
                              displayButton={(item) =>
                                item?.name ||
                                dropdownField.placeholder ||
                                t("common:dropdown_placeholder")
                              }
                              selectedItem={selectedItem?.value || undefined}
                              items={dropdownField.choices.map((choice) => ({
                                name: choice.text,
                                id: choice.value,
                              }))}
                              feedback={errors[dropdownName]?.message}
                              status={
                                errors[dropdownName] ? "error" : undefined
                              }
                              menuPosition={isPopup ? "Up" : "Down"}
                              buttonRef={ref}
                            />
                          )}
                        />
                      </div>
                    );
                  }
                  case "CHECKBOX": {
                    const checkboxField = field as CheckboxFieldLayout;
                    const checkboxNames = getCheckboxNames(checkboxField);
                    const hasError =
                      checkboxField.isRequired &&
                      isSubmitted &&
                      !checkboxNames.some((checkboxName) =>
                        Object.values(selectedCheckboxValues).some(
                          (checkbox) =>
                            !Object.prototype.hasOwnProperty.call(
                              checkbox,
                              checkboxName
                            )
                        )
                      );
                    return (
                      <div
                        key={checkboxField.databaseId}
                        className={checkboxField.cssClass ?? "flex flex-col"}
                      >
                        <p className="flex flex-initial gap-2 text-sm font-semibold uppercase text-gray-500">
                          {checkboxField.label}
                        </p>
                        {checkboxField.choices.map((choice, i) => {
                          return (
                            <Form.Checkbox
                              key={choice.text}
                              id={choice.text}
                              label={choice.text}
                              {...register(checkboxNames[i])}
                              checked={selectedCheckboxValues.some(
                                (checkboxValue) =>
                                  Object.prototype.hasOwnProperty.call(
                                    checkboxValue,
                                    checkboxNames[i]
                                  )
                              )}
                              feedback={errors[checkboxNames[i]]?.message}
                              status={
                                errors[checkboxNames[i]] ? "error" : undefined
                              }
                              onChange={(e) => {
                                handleCheckboxChange(
                                  checkboxNames[i],
                                  e.target.checked,
                                  checkboxField.label
                                );
                                checkboxNames.forEach((checkbox) =>
                                  checkbox !== checkboxNames[i]
                                    ? setValue(checkbox, false)
                                    : setValue(checkbox, true)
                                );
                              }}
                            />
                          );
                        })}
                        {hasError && (
                          <Feedback status="error">
                            {checkboxField.errorMessage || t("common:required")}
                          </Feedback>
                        )}
                      </div>
                    );
                  }
                  case "TEXTAREA": {
                    const textAreaField = field as TextAreaFieldLayout;
                    const textAreaLabel = sanitizeFieldName(
                      textAreaField.label
                    );
                    return (
                      <div
                        key={textAreaField.databaseId}
                        className={textAreaField.cssClass ?? ""}
                      >
                        <Form.TextArea
                          id={textAreaLabel}
                          label={textAreaLabel}
                          placeholder={textAreaField.placeholder}
                          maxLength={
                            !textAreaField.maxLength ||
                            textAreaField.maxLength === 0
                              ? 200
                              : textAreaField.maxLength
                          }
                          feedback={errors[textAreaLabel]?.message}
                          status={errors[textAreaLabel] ? "error" : undefined}
                          suppressHotjar
                          {...register(textAreaLabel, {
                            required: {
                              value: textAreaField.isRequired,
                              message:
                                textAreaField.errorMessage ||
                                t("common:required"),
                            },
                          })}
                        />
                      </div>
                    );
                  }
                  case "CONSENT": {
                    const consentField = field as ConsentFieldLayout;
                    return (
                      <div
                        key={consentField.databaseId}
                        className={consentField.cssClass ?? "col-span-2"}
                      >
                        <Form.Checkbox
                          id={consentField.checkboxLabel}
                          label={consentField.checkboxLabel}
                          feedback={errors.consent?.message}
                          status={errors.consent ? "error" : undefined}
                          {...register("consent", {
                            required: {
                              value: consentField.isRequired,
                              message:
                                consentField.errorMessage ||
                                t("common:required"),
                            },
                          })}
                        />
                      </div>
                    );
                  }
                  case "TEXT": {
                    const singleLineInputField =
                      field as SingleLineInputFieldLayout;
                    const singleLineInputLabel = sanitizeFieldName(
                      singleLineInputField.label
                    );
                    return (
                      <div
                        key={singleLineInputField.databaseId}
                        className={singleLineInputField.cssClass ?? ""}
                      >
                        <Form.Input
                          id={singleLineInputLabel}
                          label={singleLineInputLabel}
                          placeholder={singleLineInputField.placeholder}
                          maxLength={
                            !singleLineInputField.maxLength ||
                            singleLineInputField.maxLength === 0
                              ? 100
                              : singleLineInputField.maxLength
                          }
                          fieldError={errors[singleLineInputLabel]}
                          suppressHotjar
                          {...register(singleLineInputLabel, {
                            required: {
                              value: singleLineInputField.isRequired,
                              message:
                                singleLineInputField.errorMessage ||
                                t("common:required"),
                            },
                          })}
                        />
                      </div>
                    );
                  }
                  case "SECTION": {
                    const sectionBreakField = field as SectionBreakFieldLayout;
                    return (
                      <div
                        key={sectionBreakField.databaseId}
                        className={`col-span-2 ${sectionBreakField.cssClass}`}
                      >
                        <h5>{sectionBreakField.label}</h5>
                      </div>
                    );
                  }
                  default:
                    return null;
                }
              })}
              <div
                className={classNames(
                  `flex flex-col items-center gap-5 md:col-span-2 md:flex-row`,
                  {
                    "justify-center": isPopup,
                    "md:items-start": !isPopup,
                  }
                )}
              >
                <Button
                  isSubmit
                  buttonStyle="primary"
                  className="flex-shrink-0"
                  rightIcon={<ArrowRightIcon />}
                >
                  {submitButtonText || t("common:submit")}
                </Button>
              </div>
              <div
                className={classNames(
                  `flex flex-col justify-center gap-5 md:col-span-2 md:flex-row md:items-center md:justify-start`,
                  {
                    "md:text-left": isPopup,
                  }
                )}
              >
                <Trans t={t} i18nKey="common:general_form_disclaimer">
                  <p className="text-xs italic">
                    <TextLink href="/privacy-policy">
                      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                      <a target="_blank" />
                    </TextLink>
                    <TextLink href="/terms-conditions">
                      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                      <a target="_blank" />
                    </TextLink>
                  </p>
                </Trans>
              </div>
            </form>
          </div>
          {backgroundImageGroup && sourceUrl && (
            <FormSelectorBackgroundImage
              backgroundImageGroup={backgroundImageGroup}
              baseDomain={baseDomain}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GravityForm;
